import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, Check, X, ArrowLeftRight } from 'lucide-react';
import { format, parseISO, differenceInHours } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

export const AdminSwapApprovals = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejectionReasons, setRejectionReasons] = useState<Record<string, string>>({});

  const { data: swapRequests, isLoading } = useQuery({
    queryKey: ['matched-swap-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shift_swap_requests')
        .select(`
          *,
          requester:profiles!shift_swap_requests_requester_guard_id_fkey(full_name, email),
          target:profiles!shift_swap_requests_target_guard_id_fkey(full_name, email),
          requester_shift:team_schedules!shift_swap_requests_requester_shift_id_fkey(start_date, end_date, team_id, assigned_guards),
          target_shift:team_schedules!shift_swap_requests_target_shift_id_fkey(start_date, end_date, team_id, assigned_guards)
        `)
        .in('status', ['matched', 'approved'])
        .order('reviewed_at', { ascending: false, nullsFirst: false })
        .order('matched_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      return data || [];
    },
  });

  const matchedRequests = swapRequests?.filter((r: any) => r.status === 'matched') || [];
  const approvedRequests = swapRequests?.filter((r: any) => r.status === 'approved') || [];

  const approveManualMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const request = matchedRequests?.find((r: any) => r.id === requestId);

      const { error } = await supabase
        .from('shift_swap_requests')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq('id', requestId);

      if (error) throw error;

      // Send approval notification email in background (don't await)
      if (request && request.requester && request.target) {
        (async () => {
          try {
            // Get team names
            const { data: requesterTeam } = await supabase
              .from('teams')
              .select('name')
              .eq('id', request.requester_shift.team_id)
              .single();

            const { data: targetTeam } = await supabase
              .from('teams')
              .select('name')
              .eq('id', request.target_shift.team_id)
              .single();

            await supabase.functions.invoke('send-approval-notification', {
              body: {
                type: 'swap',
                requestId: request.id,
                requesterName: request.requester.full_name,
                requesterEmail: request.requester.email,
                targetName: request.target.full_name,
                targetEmail: request.target.email,
                requesterShift: {
                  startDate: request.requester_shift.start_date,
                  endDate: request.requester_shift.end_date,
                  teamName: requesterTeam?.name,
                  teamId: request.requester_shift.team_id,
                },
                targetShift: {
                  startDate: request.target_shift.start_date,
                  endDate: request.target_shift.end_date,
                  teamName: targetTeam?.name,
                  teamId: request.target_shift.team_id,
                },
              },
            });
            console.log('✅ Notification email sent for manual swap approval');
          } catch (emailError) {
            console.error('❌ Failed to send notification email:', emailError);
          }
        })();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matched-swap-requests'] });
      toast({
        title: 'Εγκρίθηκε (Χειροκίνητα)',
        description: 'Η αίτηση εγκρίθηκε. Παρακαλώ υλοποιήστε την αλλαγή χειροκίνητα στο ημερολόγιο.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Σφάλμα',
        description: 'Αποτυχία έγκρισης αιτήματος',
        variant: 'destructive',
      });
      console.error('Error approving swap:', error);
    },
  });

  const approveAutoMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const request = matchedRequests?.find((r: any) => r.id === requestId);
      if (!request) throw new Error('Request not found');

      console.log('🔄 Auto-approving swap request:', {
        requestId,
        requester: request.requester_guard_id,
        target: request.target_guard_id,
        requesterShiftId: request.requester_shift_id,
        targetShiftId: request.target_shift_id,
      });

      // Swap the assigned_guards arrays
      const requesterShiftGuards = request.requester_shift.assigned_guards || [];
      const targetShiftGuards = request.target_shift.assigned_guards || [];

      console.log('📊 Before swap:', {
        requesterShiftGuards,
        targetShiftGuards,
      });

      // Replace guard IDs
      const newRequesterGuards = requesterShiftGuards.map((gid: string) =>
        gid === request.requester_guard_id ? request.target_guard_id : gid
      );
      const newTargetGuards = targetShiftGuards.map((gid: string) =>
        gid === request.target_guard_id ? request.requester_guard_id : gid
      );

      console.log('📊 After swap:', {
        newRequesterGuards,
        newTargetGuards,
      });

      // Update both shifts
      const { error: error1 } = await supabase
        .from('team_schedules')
        .update({ assigned_guards: newRequesterGuards })
        .eq('id', request.requester_shift_id);

      if (error1) {
        console.error('❌ Error updating requester shift:', error1);
        throw error1;
      }
      console.log('✅ Updated requester shift');

      const { error: error2 } = await supabase
        .from('team_schedules')
        .update({ assigned_guards: newTargetGuards })
        .eq('id', request.target_shift_id);

      if (error2) {
        console.error('❌ Error updating target shift:', error2);
        throw error2;
      }
      console.log('✅ Updated target shift');

      // Mark as approved
      const { error: error3 } = await supabase
        .from('shift_swap_requests')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq('id', requestId);

      if (error3) {
        console.error('❌ Error marking request as approved:', error3);
        throw error3;
      }
      console.log('✅ Marked request as approved');

      // Send notification email in background (don't await)
      if (request.requester && request.target) {
        (async () => {
          try {
            // Get team names
            const { data: requesterTeam } = await supabase
              .from('teams')
              .select('name')
              .eq('id', request.requester_shift.team_id)
              .single();

            const { data: targetTeam } = await supabase
              .from('teams')
              .select('name')
              .eq('id', request.target_shift.team_id)
              .single();

            await supabase.functions.invoke('send-approval-notification', {
              body: {
                type: 'swap',
                requestId: request.id,
                requesterName: request.requester.full_name,
                requesterEmail: request.requester.email,
                targetName: request.target.full_name,
                targetEmail: request.target.email,
                requesterShift: {
                  startDate: request.requester_shift.start_date,
                  endDate: request.requester_shift.end_date,
                  teamName: requesterTeam?.name,
                  teamId: request.requester_shift.team_id,
                },
                targetShift: {
                  startDate: request.target_shift.start_date,
                  endDate: request.target_shift.end_date,
                  teamName: targetTeam?.name,
                  teamId: request.target_shift.team_id,
                },
              },
            });
            console.log('✅ Notification email sent for auto-approved swap');
          } catch (emailError) {
            console.error('❌ Failed to send notification email:', emailError);
          }
        })();
      }
    },
    onSuccess: () => {
      console.log('✅ Success! Invalidating queries...');
      queryClient.invalidateQueries({ queryKey: ['matched-swap-requests'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['team-schedules'] });
      toast({
        title: 'Εγκρίθηκε (Αυτόματα)',
        description: 'Η αλλαγή βαρδιών πραγματοποιήθηκε αυτόματα.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Σφάλμα',
        description: 'Αποτυχία αυτόματης εφαρμογής αλλαγής',
        variant: 'destructive',
      });
      console.error('Error auto-approving swap:', error);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const request = matchedRequests?.find((r: any) => r.id === requestId);

      const { error } = await supabase
        .from('shift_swap_requests')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
          rejection_reason: reason,
        })
        .eq('id', requestId);

      if (error) throw error;

      // Send rejection notification email in background (don't await)
      if (request && request.requester && request.target) {
        // Fire and forget - don't block the mutation
        (async () => {
          try {
            // Get team names
            const { data: requesterTeam } = await supabase
              .from('teams')
              .select('name')
              .eq('id', request.requester_shift.team_id)
              .single();

            const { data: targetTeam } = await supabase
              .from('teams')
              .select('name')
              .eq('id', request.target_shift.team_id)
              .single();

            await supabase.functions.invoke('send-rejection-notification', {
              body: {
                type: 'swap',
                requestId: request.id,
                requesterName: request.requester.full_name,
                requesterEmail: request.requester.email,
                targetName: request.target.full_name,
                targetEmail: request.target.email,
                reason: reason,
                requesterShift: {
                  startDate: request.requester_shift.start_date,
                  endDate: request.requester_shift.end_date,
                  teamName: requesterTeam?.name,
                  teamId: request.requester_shift.team_id,
                },
                targetShift: {
                  startDate: request.target_shift.start_date,
                  endDate: request.target_shift.end_date,
                  teamName: targetTeam?.name,
                  teamId: request.target_shift.team_id,
                },
              },
            });
            console.log('✅ Rejection notification email sent');
          } catch (emailError) {
            console.error('❌ Failed to send rejection notification email:', emailError);
          }
        })();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matched-swap-requests'] });
      toast({
        title: 'Απορρίφθηκε',
        description: 'Το αίτημα αλλαγής βαρδιών απορρίφθηκε.',
      });
      setRejectionReasons({});
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Αιτήματα Αμοιβαίας Αλλαγής Βαρδιών</CardTitle>
        <CardDescription>
          Εξετάστε και εγκρίνετε αιτήματα που έχουν συμφωνηθεί από αμφότερους τους υπαλλήλους. Επιλέξτε αυτόματη εφαρμογή ή χειροκίνητη έγκριση.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {matchedRequests.length > 0 || approvedRequests.length > 0 ? (
          <div className="space-y-6">
            {matchedRequests.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Προς Έγκριση</h3>
                {matchedRequests.map((request: any) => {
              const requesterHours = differenceInHours(
                parseISO(request.requester_shift.end_date),
                parseISO(request.requester_shift.start_date)
              );
              const targetHours = differenceInHours(
                parseISO(request.target_shift.end_date),
                parseISO(request.target_shift.start_date)
              );

              return (
                <div key={request.id} className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{request.requester?.full_name || 'Unknown'}</span>
                        <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{request.target?.full_name || 'Unknown'}</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <div>
                          {request.requester?.full_name || 'Unknown'}: {format(parseISO(request.requester_shift.start_date), 'PPp')} ({requesterHours}h)
                        </div>
                        <div>
                          {request.target?.full_name || 'Unknown'}: {format(parseISO(request.target_shift.start_date), 'PPp')} ({targetHours}h)
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Matched at: {format(parseISO(request.matched_at), 'PPp')}
                      </div>
                    </div>
                  </div>

                  <Textarea
                    placeholder="Αιτιολογία απόρριψης (προαιρετικό)"
                    value={rejectionReasons[request.id] || ''}
                    onChange={(e) => setRejectionReasons({
                      ...rejectionReasons,
                      [request.id]: e.target.value,
                    })}
                    className="min-h-[60px]"
                  />

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => approveAutoMutation.mutate(request.id)}
                      disabled={approveAutoMutation.isPending || approveManualMutation.isPending}
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Αυτόματη Έγκριση
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => approveManualMutation.mutate(request.id)}
                      disabled={approveAutoMutation.isPending || approveManualMutation.isPending}
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Χειροκίνητη Έγκριση
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => rejectMutation.mutate({
                        requestId: request.id,
                        reason: rejectionReasons[request.id] || 'Δεν δόθηκε αιτιολογία',
                      })}
                      disabled={rejectMutation.isPending}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Απόρριψη
                    </Button>
                  </div>
                </div>
              );
            })}
              </div>
            )}

            {approvedRequests.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Πρόσφατα Εγκριθέντα</h3>
                {approvedRequests.map((request: any) => {
                  const requesterHours = differenceInHours(
                    parseISO(request.requester_shift.end_date),
                    parseISO(request.requester_shift.start_date)
                  );
                  const targetHours = differenceInHours(
                    parseISO(request.target_shift.end_date),
                    parseISO(request.target_shift.start_date)
                  );

                  return (
                    <div key={request.id} className="rounded-lg border p-4 space-y-3 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{request.requester?.full_name || 'Unknown'}</span>
                            <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{request.target?.full_name || 'Unknown'}</span>
                            <Badge variant="secondary" className="ml-2">Εγκρίθηκε</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <div>
                              {request.requester?.full_name || 'Unknown'}: {format(parseISO(request.requester_shift.start_date), 'PPp')} ({requesterHours}h)
                            </div>
                            <div>
                              {request.target?.full_name || 'Unknown'}: {format(parseISO(request.target_shift.start_date), 'PPp')} ({targetHours}h)
                            </div>
                          </div>
                          {request.reviewed_at && (
                            <div className="text-xs text-muted-foreground">
                              Εγκρίθηκε: {format(parseISO(request.reviewed_at), 'PPp')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Δεν υπάρχουν αιτήματα προς έγκριση
          </div>
        )}
      </CardContent>
    </Card>
  );
};
